import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Table, TableCell, TableRow, TableHead, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { checkApiStatus, numberWithCommas, roundAmount } from '../../../utils';
import { updateLlpApi } from '../apiServices';
import { globalPutService } from '../../../utils/globalApiServices';
import { regexConstants } from '../../../constants/regEx';
import { trackActivity } from '../../../utils/mixpanel';

const LLPBulkEditModal = ({ bulkEditLLP, shopVisit, onToggleModal, getResponseBack, allLlpList, updateShopVisitLlpCost }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [llpList, setLlpList] = useState(bulkEditLLP?.data?.map(item => ({ ...item })) || []);
  const [fieldErrors, setFieldErrors] = useState({});

  const onFieldChange = (id, key, value) => {
    setLlpList(llpList.map(item => item?.id === id ? { ...item, [key]: value } : item));
  };

  const onFocusChange = (id) => {
    setFieldErrors(prev => {
      const { [id]: removed, ...rest } = prev;
      return rest;
    });
  };

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Shop Visit Bulk Edit LLP`, page_type: page_type, });
  };

  const onBulkEditLLP = async () => {
    const errors = {};
    llpList.forEach(item => {
      if (item?.claim_amount === '' || item?.claim_amount === null || item?.claim_amount === undefined) {
        errors[item.id] = 'Please enter Claim Amount';
      } else if (item?.sv_amount && Number(item.claim_amount) > Number(item.sv_amount)) {
        errors[item.id] = 'Cannot exceed SV Cost';
      }
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const payload = llpList.map(item => ({
      id: item?.id,
      cycles_remaining: item?.cycles_remaining ? Number(item.cycles_remaining) : 0,
      sv_amount: item?.sv_amount ? Number(item.sv_amount) : 0,
      claim_amount: item?.claim_amount ? Number(item.claim_amount) : 0,
    }));
    const editedById = new Map(payload.map(item => [item?.id, item?.sv_amount]));
    const newTotalCost = (allLlpList || []).reduce((sum, item) => sum + (editedById.has(item?.id) ? editedById.get(item?.id) : (Number(item?.sv_amount) || 0)), 0);
    const hasPrsv = shopVisit?.events?.some((ev) => ev?.maintenance_program?.is_llp_event === false);
    const prsvTotal = Number(shopVisit?.events?.find((ev) => ev?.maintenance_program?.is_llp_event === false)?.cost) || 0;
    const svCost = Number(shopVisit?.cost) || 0;
    if (newTotalCost > svCost || prsvTotal > svCost || (newTotalCost + prsvTotal) > svCost) {
      const parts = [`LLP: ${newTotalCost}`];
      if (hasPrsv) parts.push(`PRSV: ${prsvTotal}`);
      if (hasPrsv) parts.push(`Total: ${newTotalCost + prsvTotal}`);
      parts.push(`Max: ${svCost}`);
      enqueueSnackbar(`Cost exceeds shop visit limit (${parts.join(', ')}).`, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
      return;
    }
    setLoading(true);
    const svSyncResult = await updateShopVisitLlpCost(shopVisit, newTotalCost);
    if (!svSyncResult) {
      setLoading(false);
      return;
    }
    globalPutService(updateLlpApi(shopVisit?.asset_id), payload).then(async (response) => {
      if (checkApiStatus(response)) {
        await getResponseBack();
        setLoading(false);
        onToggleModal();
        trackActivity('Item Edited', { page_title: `Bulk Edit Shop Visit LLP`, success_msg: response?.data?.message, payload: payload, response: response?.data });
      } else {
        setLoading(false);
        enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
        trackActivity('Item Edited', { page_title: `Bulk Edit Shop Visit LLP`, error_msg: response?.data?.message, payload: payload, response: response?.data });
      }
    });
  };

  return (
    <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' maxWidth='lg' fullWidth open={bulkEditLLP?.modal}>
      <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>Bulk Edit LLP</DialogTitle>
      <DialogContent dividers>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table className='mui-table-format' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>Module</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>Description</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>Part Number</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Cycles Max Limit</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Cycles Remaining</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>SV Cost</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100 }}>Claim Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {llpList.map((item) => (
                <TableRow key={item?.id}>
                  <TableCell>{item?.module ?? '--'}</TableCell>
                  <TableCell>{item?.description ?? '--'}</TableCell>
                  <TableCell>{item?.part_number ?? '--'}</TableCell>
                  <TableCell>{item?.cycles_max_limit !== null && item?.cycles_max_limit !== undefined && item?.cycles_max_limit !== '' ? numberWithCommas(roundAmount(item.cycles_max_limit)) : '--'}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size='small'
                      variant='outlined'
                      value={item?.cycles_remaining ?? ''}
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) || e.target.value === '' ? onFieldChange(item?.id, 'cycles_remaining', e.target.value.trimStart()) : e.preventDefault(); }}
                      slotProps={{ htmlInput: { maxLength: 255 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size='small'
                      variant='outlined'
                      value={shopVisit?.status?.value === 3 && item?.sv_amount !== '' && item?.sv_amount !== null && item?.sv_amount !== undefined ? numberWithCommas(roundAmount(item?.sv_amount)) : item?.sv_amount ?? ''}
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) || e.target.value === '' ? onFieldChange(item?.id, 'sv_amount', e.target.value.trimStart()) : e.preventDefault(); }}
                      slotProps={{ htmlInput: { maxLength: 255 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                      disabled={shopVisit?.status?.value === 3}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size='small'
                      variant='outlined'
                      value={item?.claim_amount ?? ''}
                      onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) || e.target.value === '' ? onFieldChange(item?.id, 'claim_amount', e.target.value.trimStart()) : e.preventDefault(); }}
                      onFocus={() => onFocusChange(item?.id)}
                      slotProps={{ htmlInput: { maxLength: 255 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                      required
                      error={!!fieldErrors[item.id]}
                      helperText={fieldErrors[item.id] || ''}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' size='small' disabled={isLoading} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button'); }}> Cancel </Button>
        <Button variant='contained' className='btn-primary' size='small' disabled={isLoading} onClick={() => { onBulkEditLLP(); handleItemClicked('Save Button'); }}>
          {isLoading ? <CircularProgress color='inherit' size={25} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LLPBulkEditModal;
